import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import L from "leaflet";
import { MapContainer, TileLayer } from "react-leaflet";
import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";
import { message, Spin, Typography } from "antd";
import { AimOutlined, EnvironmentOutlined, SearchOutlined } from "@ant-design/icons";
import InputItem from "../InputItem";
import ButtonItem from "../ButtonItem";
import {
    CENTRO_DEFAULT,
    ZOOM_DEFAULT,
    ZOOM_CALLE,
    CAPA_OSM,
    areaALatLngs,
    latLngsAArea,
} from "./leafletConfig";

const { Text } = Typography;

const ESTILO_POLIGONO = { color: "#00BFD6", weight: 2, fillOpacity: 0.2 };

// Control de formulario: value/onChange con un Polygon GeoJSON [lng, lat]
const MapaPerimetroEditor = ({ value, onChange, height = 380 }) => {
    const headersToken = useSelector((state) => state.user.headersToken);
    const [mapa, setMapa] = useState(null);
    const [query, setQuery] = useState("");
    const [resultados, setResultados] = useState([]);
    const [buscando, setBuscando] = useState(false);

    const capaRef = useRef(null);
    // Último valor emitido por el mapa, para no redibujar encima de la edición
    const ultimoEmitidoRef = useRef(null);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const emitir = useCallback((area) => {
        ultimoEmitidoRef.current = area ? JSON.stringify(area.coordinates) : null;
        if (onChangeRef.current) onChangeRef.current(area);
    }, []);

    const registrarCapa = useCallback((capa) => {
        capaRef.current = capa;
        capa.on("pm:edit", () => emitir(latLngsAArea(capa.getLatLngs()[0])));
        capa.on("pm:remove", () => {
            capaRef.current = null;
            emitir(undefined);
        });
    }, [emitir]);

    useEffect(() => {
        if (!mapa) return;

        mapa.pm.setLang("es");
        mapa.pm.setGlobalOptions({ allowSelfIntersection: false, pathOptions: ESTILO_POLIGONO });
        mapa.pm.addControls({
            position: "topleft",
            drawMarker: false,
            drawCircleMarker: false,
            drawPolyline: false,
            drawCircle: false,
            drawText: false,
            cutPolygon: false,
            rotateMode: false,
        });

        // Solo se permite un polígono: dibujar uno nuevo reemplaza al anterior
        const alCrear = (e) => {
            if (capaRef.current) mapa.removeLayer(capaRef.current);
            registrarCapa(e.layer);
            emitir(latLngsAArea(e.layer.getLatLngs()[0]));
        };
        mapa.on("pm:create", alCrear);

        // Dentro de un modal el contenedor cambia de tamaño después de montarse
        const observador = new ResizeObserver(() => mapa.invalidateSize());
        observador.observe(mapa.getContainer());

        return () => {
            observador.disconnect();
            mapa.off("pm:create", alCrear);
        };
    }, [mapa, emitir, registrarCapa]);

    useEffect(() => {
        if (!mapa) return;
        const clave = value?.coordinates ? JSON.stringify(value.coordinates) : null;
        if (clave === ultimoEmitidoRef.current) return;
        ultimoEmitidoRef.current = clave;

        if (capaRef.current) {
            mapa.removeLayer(capaRef.current);
            capaRef.current = null;
        }

        const latLngs = areaALatLngs(value);
        if (!latLngs) return;
        const capa = L.polygon(latLngs, ESTILO_POLIGONO).addTo(mapa);
        registrarCapa(capa);
        mapa.fitBounds(capa.getBounds(), { padding: [20, 20] });
    }, [mapa, value, registrarCapa]);

    const buscar = async () => {
        if (!query.trim()) return;
        setBuscando(true);
        try {
            const resp = await axios.post(
                "perimetrosChecada/buscarDireccion",
                { query },
                { headers: headersToken },
            );
            if (resp.data.success !== 2) {
                message.error(resp.data.message || "No se pudo buscar la dirección.");
                setResultados([]);
                return;
            }
            const encontrados = resp.data.resultados || [];
            setResultados(encontrados);
            if (!encontrados.length) message.info("No se encontró la dirección.");
        } catch {
            message.error("No se pudo buscar la dirección.");
        } finally {
            setBuscando(false);
        }
    };

    const elegir = (resultado) => {
        setResultados([]);
        if (!mapa) return;
        // boundingbox viene como [sur, norte, oeste, este]
        const [sur, norte, oeste, este] = (resultado.boundingbox || []).map(Number);
        if ([sur, norte, oeste, este].every(Number.isFinite)) {
            mapa.fitBounds([[sur, oeste], [norte, este]], { maxZoom: ZOOM_CALLE });
        } else {
            mapa.setView([Number(resultado.lat), Number(resultado.lon)], ZOOM_CALLE);
        }
    };

    const usarMiUbicacion = () => {
        if (!navigator.geolocation) return message.error("Tu navegador no soporta geolocalización.");
        setBuscando(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setBuscando(false);
                if (mapa) mapa.setView([pos.coords.latitude, pos.coords.longitude], ZOOM_CALLE);
            },
            () => {
                setBuscando(false);
                message.error("No se pudo obtener tu ubicación.");
            },
        );
    };

    return (
        <div>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <InputItem
                    placeholder="Buscar dirección o lugar..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onPressEnter={(e) => {
                        // Enter no debe mandar el formulario que contiene al mapa
                        e.preventDefault();
                        buscar();
                    }}
                />
                <ButtonItem cyan icon={<SearchOutlined />} onClick={buscar} />
                <ButtonItem blanco icon={<AimOutlined />} onClick={usarMiUbicacion}>
                    Usar mi ubicación
                </ButtonItem>
            </div>

            {buscando && (
                <div style={{ textAlign: "center", padding: 6 }}>
                    <Spin size="small" />
                </div>
            )}

            {resultados.length > 0 && (
                <div
                    style={{
                        marginBottom: 8,
                        maxHeight: 160,
                        overflowY: "auto",
                        border: "1px solid #e8e8e8",
                        borderRadius: 8,
                    }}
                >
                    {resultados.map((r) => (
                        <div
                            key={r.place_id}
                            onClick={() => elegir(r)}
                            style={{ padding: "8px 10px", cursor: "pointer", borderBottom: "1px solid #f0f0f0" }}
                        >
                            <EnvironmentOutlined style={{ color: "#fd9e62", marginRight: 6 }} />
                            <Text style={{ fontSize: 13 }}>{r.display_name}</Text>
                        </div>
                    ))}
                </div>
            )}

            <MapContainer
                center={CENTRO_DEFAULT}
                zoom={ZOOM_DEFAULT}
                style={{ height, width: "100%", borderRadius: 8 }}
                whenCreated={setMapa}
            >
                <TileLayer url={CAPA_OSM.url} attribution={CAPA_OSM.attribution} maxZoom={CAPA_OSM.maxZoom} />
            </MapContainer>

            <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
                Dibuja el perímetro con el botón de polígono (o rectángulo) de la izquierda. Puedes mover los
                vértices con el lápiz o borrarlo con el bote de basura. Dibujar uno nuevo reemplaza al anterior.
            </div>
        </div>
    );
};

export default MapaPerimetroEditor;
